import { aircraftType, cabinType, getAircraftType, getEmissionText, getFlightEmissionsData, getStopMetadata, insertIf, join, ticketTypeIcon, tripGetToAndFromCity } from '@js/components/resultsRenderHelpers';
import { l, lreplace, linkPrefix } from '@js/utils/toolbox';
import { renderResultAgents } from './resultsRenderTemplate';

/**
 *
 * @param {Array} journeys
 * @param {Object} options
 * @param {Object} options.filters
 * @param {Boolean} options.showEmissions
 *
 * @returns
 */
export function renderModal (journeys = [], options = {}) {
	const { filters = {}, showEmissions = false } = options;

	return join(journeys, journey => `
    <div class="abResult__detailed">
      ${join(journey.trips, trip => renderTrip(trip, filters))}
      ${insertIf(showEmissions, () => renderEmissions(journey))}
      ${insertIf(journey.agents.length, () => renderOTAs(journey))}
    </div>
  `);
}


function renderTrip (trip, filters) {
	const { fromCity, toCity } = tripGetToAndFromCity(trip);
	return `
    <div class="abResult__head">
      <div>
        <span class="icon icon-circle-plane-${trip.bound === 'outbound' ? 'up' : 'down'}"></span>
        <span>${fromCity} – ${toCity}</span>
      </div>
      <div><span>${trip.totalTravelTime}</span></div>
    </div>

    <div class="abResult__legs">
      ${join(trip.segment, (segment, idx) => `
        ${renderLeg(segment, filters)}
        ${insertIf(idx < trip.segment.length - 1, () => renderStop(segment))}
      `)}
    </div>
  `;
}

function renderLeg (segment, filters) {
	return `
    <div class="abResult__leg">
      ${renderLegAirlinePicture(segment)}
      <div class="abResult__leg--divider"><div class="abResult__line"></div></div>
      ${renderLegDates(segment)}
      <div class="abResult__leg--divider-second"><div class="abResult__line"></div></div>
      ${renderLegDestinations(segment)}
    </div>
    ${renderLegSummaries(segment, filters)}
  `;
}

function renderLegAirlinePicture (segment) {
	return `
    <div class="abResult__leg--airlinepicture">
      <img
        src="${linkPrefix.assetPrefix}/images/airlines/iata/small/${segment.airlineCode}.png"
        class="abResult__airlineImg"
        title="${segment.airline}" alt="${segment.airline}"
        onerror="this.style.display='none'" />
      <div class="abResult__flightTime"><span>${segment.flightTime}</span></div>
    </div>`;
}

function renderLegDates (segment) {
	return `
    <div class="abResult__leg--dates">
      <div>
        <div class="abResult__smallLabel"><span>${segment.localizedDepartureDayShortString}</span></div>
        <div><span class="abResult__bigLabel">${segment.departureTime}</span></div>
      </div>
      <div>
        <div class="abResult__smallLabel"><span>${segment.localizedArrivalDayShortString}</span></div>
        <div><span class="abResult__bigLabel">${segment.arrivalTime}</span></div>
      </div>
    </div>`;
}

function renderLegDestinations (segment) {
	return `
    <div class="abResult__leg--destinations">
      <div class="abResult__leg--destination">
        <div class="abResult__smallLabel">
          <span>${segment.departureAirportName}, ${segment.departureAirport}</span>
        </div>
        <div><span class="abResult__bigLabel">${segment.departureCity}</span></div>
      </div>
      <div class="abResult__leg--destination">
        <div class="abResult__smallLabel">
          <span>${segment.arrivalAirportName}, ${segment.arrivalAirport}</span>
        </div>
        <div><span class="abResult__bigLabel">${segment.arrivalCity}</span></div>
      </div>
    </div>`;
}

function renderLegSummaries (segment, filters) {
	return `
    <div class="abResult__summaries">
      <div class="abResult__flightTime"><span>${segment.flightTime}</span></div>
      <div class="abResult__segmentInfo">
        ${renderCabin(segment)}
        ${insertIf(filters.flexTicket, renderFlexTicket)}
        ${insertIf(filters.baggageIncluded, () => renderBaggage(filters.baggageIncluded))}
        ${renderFlightInfo(segment)}
      </div>
    </div>`;
}

function renderCabin (segment) {
	return `
    <div
      class="abResult__segmentInfo--cabin ${insertIf(segment.unmetClassType, () => 'mixedCabinType')}"
      ${insertIf(segment.unmetClassType, () => `title="${l('showresult.tooltip.combinedTicket')}"`)}
    >
      <span class="${ticketTypeIcon(segment.cabin)}"></span>
      <span ${insertIf(segment.unmetClassType, 'class="cabin-text--special"')}>${cabinType(segment.cabin)}</span>
      ${insertIf(segment.unmetClassType, () => `<span class="icon icon-circle-question-mark icon-with-tooltip" data-tooltip="${l('showresult.tooltip.combinedTicket')}"></span>`)}
    </div>`;
}

const renderFlexTicket = () => `<div><span class="icon icon-rebooking" style="font-weight:900"></span><span>${l('global.simplePhrases.rebooking')}</span></div>`;

const renderBaggage = bags => `<div class="abResult__summary"><span class="icon icon-checkin-luggage"></span><span>${lreplace('showresult.result.checkedInBagsIncluded', { bags })}</span></div>`;

function renderFlightInfo (segment) {
	return `
    <div>
      <span class="icon-plane"></span>
      <span>${segment.airline}</span>
      <span>${segment.flightNo}</span>
      <span>${getAircraftType(aircraftType(segment.equipment))}</span>
    </div>`;
}

/* -------------------------------------------------------------------------- */
/*                                   OTAs                                     */
/* -------------------------------------------------------------------------- */

function renderOTAs (journey) {
	const common = {
		resultIndex: journey.resultIndex,
		agents: journey.agents,
		isModal: true,
		climateCompensationPrice: journey?.climateCompensationPrice || 0,
		promotedAgent: journey.promotedAgent,
		promotedProduct: journey.promotedProduct,
		source: journey.source,
		hasPromotedAgent: journey?.promotedAgent && journey?.promotedProduct
	};

	return `
    <div class="abResult__otas">
      ${renderResultAgents({ ...common, extended: false })}
      ${renderResultAgents({ ...common, extended: true })}
    </div>`;
}

function renderEmissions (journey) {
	const {
		isFullyCompensated,
		isEmissionAbnormallyHigh,
		hasAirlineCompensation,
		airlineCompensation,
		userCompensation,
		emissionLegend,
		emissionValueWithUnit,
		emissionDefinition,
		isSortedByNet,
		emissionValue
	} = getFlightEmissionsData(journey.raw_emission, journey.compensated_emission, journey.emission);

	return `
    <div class="abResult__emission" data-emission="${emissionValue}">
      <div class="emission__header emission__row">
        <div class="emission__col emission__col--climateCompensation"><span class="emission__title">${getEmissionText(journey)}</span></div>
      </div>
      <div class="emission__main emission__row ${insertIf(isEmissionAbnormallyHigh, 'emission--error')}">
        <div class="emission__col emission__col--carbonFootprint">
          <img src="${linkPrefix.assetPrefix}/images/global/CO2-cloud.png" alt="">
          <span>${emissionValueWithUnit}${insertIf(isSortedByNet, '*')}</span>
        </div>
        <div class="emission__col emission__col--climateCompensation compensationDistribution ${insertIf(!isFullyCompensated, 'compensation-user')}"
             data-airline="${airlineCompensation}"
             data-user="${userCompensation}">
          <div class="compensationDistribution__arrow compensationDistribution__arrow--airline ${insertIf(hasAirlineCompensation, 'compensationDistribution__arrow--visible')}" style="width:${airlineCompensation}%"></div>
          <div class="compensationDistribution__arrow compensationDistribution__arrow--user ${insertIf(!isFullyCompensated, 'compensationDistribution__arrow--visible')} ${insertIf(!hasAirlineCompensation, 'compensationDistribution__arrow--unique')}" style="width:${userCompensation}%"></div>
          <div class="compensationDistribution__legend">${emissionLegend}</div>
        </div>
      </div>
      ${insertIf(isSortedByNet, `<div class="emission__footer emission__row">${emissionDefinition}</div>`)}
    </div>`;
}

function renderStop (seg) {
	const { styles, icon, stopText } = getStopMetadata(seg);
	return `
    <div class="abResult__stop ${styles}">
      <div class="abResult__stopTime"><span>${seg.waitTime}</span></div>
      <span class="icon ${icon}"></span>
      <span class="abResult__stopTxt">${stopText}</span>
    </div>`;
}
